import styles from '../track-grid.module.css'

import React, { useCallback, useEffect, useMemo } from 'react'

import { TRACK } from '@disco/constants/ItemTypes'
import { PLAYING } from '@disco/constants/MediaStatus'
import type { TrackOptions } from '@disco/state-management/actions/MediaActions'
import {
  getTrackWaveform,
  playTrack,
  setTrackMediaEventProperties,
} from '@disco/state-management/actions/MediaActions'
import {
  getTrackPlaybackStatus,
  getTrackWaveformStatus,
} from '@disco/state-management/selectors/media'
import { getDiscoverPlayAudioMixpanelProperties } from '@disco/utils/mixpanel/discover-page'
import type { DiscoverPlayAudioTypes } from '@disco/utils/mixpanel/types'
import { formatDuration } from '@disco/utils/time-utils'

import type { Track } from 'types/track'

import { getAuthTransferUrl } from 'utils/routing'

import MediaOverlay from 'components/common/media-overlay'
import TrackArtwork from 'components/common/tracks/track-artwork'
import { Box, Link } from 'components/ds'
import OptimizedWaveform from 'components/search/search-columns/waveform-column/OptimizedWaveform'
import Draggable from 'components/ui/draggable'
import { HoverProvider } from 'components/ui/hover-provider'
import { ScrollingText } from 'components/ui/scrolling-text'

import { useDispatch, useSelector } from 'hooks/use-redux'

import TrackNumberDragHandle from '../../../../components/track-number-drag-handle'
import type { ContentType } from '../../../../utils'
import { parseWaveformData } from '../../../../utils'

type ContentTrackRowProps = {
  track: Track
  index: number
  playbackContext: TrackOptions
  contentType: ContentType
  contentId: number
  contentName?: string
  businessName?: string
  mixpanelProperties?: Partial<DiscoverPlayAudioTypes>
}

export const ContentTrackRow = ({
  track,
  index,
  playbackContext,
  contentType,
  contentId,
  mixpanelProperties,
}: ContentTrackRowProps) => {
  const dispatch = useDispatch()

  // Derived values - compute early so hooks can use them
  const title = track?.name || 'Unknown Track'
  const artist = track?.artist || ''
  const catalogName = track?.client_library_name
  const catalogTransferUrl = track?.client_library_id
    ? getAuthTransferUrl(track.client_library_id)
    : undefined
  const customUrl = track?.custom_url
  const customUrlLabel = track?.custom_url_name
  const duration = track?.duration
    ? formatDuration(track.duration, 'm:ss')
    : '--:--'
  const mixpanelContext: 'Curated Playlist' | 'Preview Tracks' =
    contentType === 'playlist' ? 'Curated Playlist' : 'Preview Tracks'
  const sourceGroupType =
    contentType === 'playlist' ? 'curated_playlist' : 'client_library'

  // Get waveform status
  const waveformStatus = useSelector(state =>
    getTrackWaveformStatus(state, { trackId: track?.id }),
  )

  // Get playback status to check if track is playing
  const playbackStatus = useSelector(state =>
    getTrackPlaybackStatus(state, { trackId: track?.id }),
  )
  const isTrackPlaying = playbackStatus?.playbackStatus === PLAYING

  // Shared Mixpanel event payload
  const mixpanelPayload = useMemo(() => {
    return {
      trackId: track?.id,
      trackName: track?.name,
      ...mixpanelProperties,
      context: mixpanelContext,
    }
  }, [track?.id, track?.name, mixpanelProperties, mixpanelContext])

  const handleMixpanelAudioEventClick = useCallback(() => {
    const trackId = track?.id
    if (!mixpanelPayload || !trackId) return
    const properties = getDiscoverPlayAudioMixpanelProperties(mixpanelPayload)
    dispatch(
      setTrackMediaEventProperties({
        trackId: trackId,
        properties,
      }),
    )
  }, [dispatch, track?.id, mixpanelPayload])

  // Parse waveform data - try track's own data first, then fall back to Redux
  const parsedWaveformData = useMemo(() => {
    const trackWaveform = parseWaveformData(track?.waveform)
    if (trackWaveform) return trackWaveform

    // Fall back to Redux waveform data if available (for catalog tracks)
    if (waveformStatus?.waveformData) {
      return waveformStatus.waveformData
    }

    return null
  }, [track?.waveform, waveformStatus?.waveformData])

  // Load waveform if not available
  const trackId = track?.id
  const trackWaveform = track?.waveform
  const trackDuration = track?.duration

  useEffect(() => {
    if (trackId && !trackWaveform && !waveformStatus && trackDuration > 0) {
      dispatch(getTrackWaveform(trackId))
    }
  }, [trackId, trackWaveform, trackDuration, waveformStatus, dispatch])

  // Handle waveform click - start playback if not playing
  const handleWaveformSeek = useCallback(() => {
    if (!track?.id || isTrackPlaying) return
    dispatch(playTrack(track.id, 'in-app', playbackContext))
    handleMixpanelAudioEventClick()
  }, [
    track.id,
    isTrackPlaying,
    dispatch,
    playbackContext,
    handleMixpanelAudioEventClick,
  ])

  // Skip if track is invalid - after all hooks
  if (!track || !track.id) {
    return null
  }

  // Drag preview without waveform
  const dragPreview = (
    <div className={styles.dragPreview}>
      <span className={styles.trackNumber}>{index + 1}</span>
      <Box className={styles.artworkContainer}>
        <TrackArtwork track={track} size="160" />
      </Box>
      <div className={styles.trackTitleGroup}>
        <span className={styles.trackTitle}>{title}</span>
        <span className={styles.trackArtist}>{artist}</span>
      </div>
      <span className={styles.duration}>{duration}</span>
    </div>
  )

  return (
    <Draggable
      id={`discover-track-${track.id}`}
      data={{
        track,
        sourceGroupId: contentId,
        sourceGroupType,
        ...mixpanelPayload,
      }}
      type={TRACK}
      preview={dragPreview}
    >
      <div className={styles.trackRow}>
        <TrackNumberDragHandle index={index} />

        <HoverProvider>
          <Box className={styles.artworkContainer}>
            <MediaOverlay
              track={track}
              mediaSource="in-app"
              location="discover-music-home"
              playbackContext={playbackContext}
              iconSize={12}
              clickEvent={handleMixpanelAudioEventClick}
            >
              <TrackArtwork track={track} size="160" />
            </MediaOverlay>
          </Box>
        </HoverProvider>

        <div className={styles.trackTitleGroup} translate="no">
          <span className={styles.trackTitle}>{title}</span>
          <span className={styles.trackArtist}>{artist}</span>
        </div>

        <ScrollingText className={styles.trackBusiness}>
          {catalogName && catalogTransferUrl ? (
            <Link
              href={catalogTransferUrl}
              target="_blank"
              rel="noopener noreferrer"
              color="gray"
              underline="hover"
              onClick={event => event.stopPropagation()}
            >
              {catalogName}
            </Link>
          ) : customUrl ? (
            <Link
              href={customUrl}
              target="_blank"
              rel="noopener noreferrer"
              color="gray"
              underline="hover"
              onClick={event => event.stopPropagation()}
            >
              {customUrlLabel ? customUrlLabel : 'Open link'}
            </Link>
          ) : null}
        </ScrollingText>

        <div className={styles.waveformContainer}>
          {parsedWaveformData ? (
            <OptimizedWaveform
              className={styles.waveform}
              track={track}
              parsedWaveformData={parsedWaveformData}
              barGap={2}
              barWidth={1}
              isHoverSeekEnabled
              height={24}
              waveformLocation="Discover Music Home"
              noAnimation
              onSeek={handleWaveformSeek}
            />
          ) : (
            <div className={styles.waveformPlaceholder} />
          )}
        </div>

        <span className={styles.duration}>{duration}</span>
      </div>
    </Draggable>
  )
}

export default ContentTrackRow
